import React from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import moment from '@g4/moment-utc';

import { Box, VerticalSpacing, useMedia } from '../../../Boneyard';
import Flight, { legTypes } from './flightInfo/Flight';

import PLANE_ICON from '../../../../images/ic-plane-blue.svg';

import {
  CART_FLIGHT_DEPARTING,
  CART_FLIGHT_RETURNING,
} from '../../../../../automationHooks.json';

const propTypes = {
  flights: PropTypes.array,
  isTripSummary: PropTypes.bool,
};

const DashedLine = withTheme(({ length, theme }) => {
  const STROKE_WIDTH = 2;
  const CAP_OFFSET = STROKE_WIDTH / 2;

  return (
    <svg
      viewBox={`0 0 ${STROKE_WIDTH} ${length}`}
      width={STROKE_WIDTH}
      xmlns="http://www.w3.org/2000/svg"
    >
      <line
        x1={STROKE_WIDTH / 2}
        x2={STROKE_WIDTH / 2}
        y1={CAP_OFFSET}
        y2={length - CAP_OFFSET}
        strokeWidth={STROKE_WIDTH}
        strokeDasharray={`${STROKE_WIDTH * 2} ${STROKE_WIDTH * 2}`}
        strokeLinecap="round"
        stroke={theme.colors.common.peacockBlue}
      />
    </svg>
  );
});

DashedLine.propTypes = {
  theme: PropTypes.shape({
    colors: PropTypes.shape({
      common: PropTypes.shape({
        light: PropTypes.string.isRequired,
      }),
    }),
  }),
};

const PlaneIcon = styled.img.attrs({
  src: PLANE_ICON,
  alt: '',
})`
  height: ${props =>
    props.isTripSummary ? (props.isMobile ? '30px' : '40px') : '20px'};

  ${props => props.flipped && `transform: scale(-1, 1)`};
`;

const FlightInfo = ({ flights, isTripSummary }) => {
  const mq = useMedia();
  const isMobile = mq.lte('md');
  const sortedFlights = [...flights].sort((a, b) => {
    const timeA = moment(a.flight.departingTime);
    const timeB = moment(b.flight.departingTime);
    return timeA.isValid() && timeB.isValid() ? timeA.diff(timeB) : 0;
  });

  return (
    <Box d="flex" pt={1} pl={3} w="100%">
      <Box d="flex" direction="column" alignItems="center">
        {sortedFlights.length === 1 ? (
          <PlaneIcon isTripSummary={isTripSummary} isMobile={isMobile} />
        ) : (
          <React.Fragment>
            <PlaneIcon isTripSummary={isTripSummary} isMobile={isMobile} />
            <DashedLine length={isTripSummary ? (isMobile ? 42 : 80) : 50} />
            <PlaneIcon
              flipped
              isTripSummary={isTripSummary}
              isMobile={isMobile}
            />
          </React.Fragment>
        )}
      </Box>

      <Box
        d="flex"
        direction="column"
        justifyContent="space-between"
        mt={isTripSummary ? (isMobile ? 1 : 2) : 0}
      >
        {sortedFlights.map((data, index) => (
          <React.Fragment key={data.id}>
            <Flight
              leg={index === 0 ? legTypes.DEPARTING : legTypes.RETURNING}
              dateTime={data.flight.departingTime}
              origin={data.flight.origin}
              destination={data.flight.destination}
              isOvernight={data.flight.isOvernight}
              isTripSummary={isTripSummary}
              automationHook={
                index === 0
                  ? CART_FLIGHT_DEPARTING.hook
                  : CART_FLIGHT_RETURNING.hook
              }
            />
            {index < sortedFlights.length - 1 && (
              <VerticalSpacing size={isTripSummary ? (isMobile ? 4 : 10) : 5} />
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

FlightInfo.propTypes = propTypes;

export default FlightInfo;
